import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Copy, Upload, Search, FileDown, FileUp } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

const EMPTY = { category_id: "", text: "", options: ["", "", "", ""], correct_index: 0, explanation: "", points: 100, timer: 20, difficulty: "medium", active: true };

export default function Questions() {
  const [data, setData] = useState({ questions: [], total: 0, pages: 1 });
  const [cats, setCats] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [importOpen, setImportOpen] = useState(false);
  const [importCat, setImportCat] = useState("");
  const [csvText, setCsvText] = useState("");
  const [csvFileName, setCsvFileName] = useState("");

  const downloadTemplate = () => {
    const template = [
      "question,option_a,option_b,option_c,option_d,correct,explanation,points,timer,difficulty",
      "Which year did India gain independence?,1945,1946,1947,1950,c,India gained independence on 15 August 1947.,100,20,easy",
      "Who is known as the Iron Man of India?,Jawaharlal Nehru,Sardar Vallabhbhai Patel,B. R. Ambedkar,Lal Bahadur Shastri,b,Patel unified 562 princely states.,100,20,medium",
    ].join("\n");
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lostdc_questions_template.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Template downloaded — fill it and upload below");
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a .csv file");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCsvText(String(reader.result || ""));
      setCsvFileName(file.name);
      toast.success(`${file.name} loaded — click Import to proceed`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const load = async (p = page) => {
    try {
      const { data } = await api.get("/admin/questions", { params: { category_id: catFilter === "all" ? "" : catFilter, search, page: p, limit: 15 } });
      setData(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => {
    api.get("/admin/categories").then(({ data }) => setCats(data.categories)).catch(() => {});
  }, []);
  useEffect(() => { load(1); setPage(1); /* eslint-disable-next-line */ }, [catFilter]);

  const save = async () => {
    try {
      const payload = { ...form, points: Number(form.points), timer: Number(form.timer), correct_index: Number(form.correct_index) };
      if (editing === "new") await api.post("/admin/questions", payload);
      else await api.patch(`/admin/questions/${editing.id}`, payload);
      toast.success("Question saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (q) => {
    if (!window.confirm("Delete this question?")) return;
    await api.delete(`/admin/questions/${q.id}`).then(() => { toast.success("Deleted"); load(); }).catch((e) => toast.error(apiError(e)));
  };

  const dup = async (q) => {
    await api.post(`/admin/questions/${q.id}/duplicate`).then(() => { toast.success("Duplicated"); load(); }).catch((e) => toast.error(apiError(e)));
  };

  const doImport = async () => {
    if (!importCat || !csvText.trim()) { toast.error("Pick a category and paste CSV"); return; }
    try {
      const { data } = await api.post("/admin/questions/import", { category_id: importCat, csv_text: csvText });
      toast.success(`Imported ${data.created} questions${data.errors.length ? ` (${data.errors.length} errors)` : ""}`);
      setImportOpen(false); setCsvText(""); setCsvFileName("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const catName = (id) => cats.find((c) => c.id === id)?.name || id;

  return (
    <div data-testid="admin-questions">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black font-heading text-slate-900 tracking-tighter">Question Bank</h1>
          <p className="text-sm text-slate-500 mt-1">{data.total} questions. Correct answers are never sent to participants.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setImportOpen(true)} data-testid="import-questions-btn" className="px-4 py-2.5 rounded-lg border border-slate-300 text-sm font-bold text-slate-600 hover:border-slate-500 transition-colors inline-flex items-center gap-2"><Upload className="w-4 h-4" /> CSV Import</button>
          <button onClick={() => { setForm({ ...EMPTY, category_id: catFilter !== "all" ? catFilter : cats[0]?.id || "" }); setEditing("new"); }} data-testid="add-question-btn" className="px-5 py-2.5 rounded-lg bg-[#FF9933] text-white text-sm font-bold hover:bg-[#E6892E] transition-colors inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add Question</button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="question-search" className="pl-9" placeholder="Search question text…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(1)} />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger data-testid="question-cat-filter" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5 space-y-3">
        {data.questions.map((q) => (
          <div key={q.id} className="bg-white border border-slate-200 rounded-xl p-5 flex items-start justify-between gap-4" data-testid={`question-row-${q.id}`}>
            <div className="min-w-0">
              <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-slate-400">{catName(q.category_id)} • {q.timer}s • {q.points} pts • {q.difficulty}</div>
              <div className="font-semibold text-slate-800 mt-1">{q.text}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {q.options.map((o, i) => (
                  <span key={i} className={`text-xs px-2.5 py-1 rounded-md font-semibold ${i === q.correct_index ? "bg-[#138808]/10 text-[#138808]" : "bg-slate-100 text-slate-500"}`}>{String.fromCharCode(65 + i)}. {o}</span>
                ))}
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button onClick={() => dup(q)} data-testid={`dup-q-${q.id}`} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors"><Copy className="w-4 h-4" /></button>
              <button onClick={() => { setForm({ category_id: q.category_id, text: q.text, options: [...q.options], correct_index: q.correct_index, explanation: q.explanation || "", points: q.points, timer: q.timer, difficulty: q.difficulty, active: q.active }); setEditing(q); }} data-testid={`edit-q-${q.id}`} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(q)} data-testid={`del-q-${q.id}`} className="p-2 rounded-lg text-red-400 hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {data.questions.length === 0 && <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400 text-sm">No questions found.</div>}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <button disabled={page <= 1} onClick={() => { setPage(page - 1); load(page - 1); }} data-testid="questions-prev" className="text-sm font-bold text-slate-500 disabled:opacity-30">← Prev</button>
        <span className="text-xs text-slate-400 font-semibold">Page {page} / {data.pages}</span>
        <button disabled={page >= data.pages} onClick={() => { setPage(page + 1); load(page + 1); }} data-testid="questions-next" className="text-sm font-bold text-slate-500 disabled:opacity-30">Next →</button>
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="question-form-modal">
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "New Question" : "Edit Question"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Category</label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="q-category" className="mt-1.5"><SelectValue placeholder="Pick category" /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Question</label><Textarea data-testid="q-text" className="mt-1.5" rows={2} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} /></div>
            {form.options.map((o, i) => (
              <div key={i} className="flex items-center gap-3">
                <button type="button" onClick={() => setForm({ ...form, correct_index: i })} data-testid={`q-correct-${i}`}
                  className={`w-9 h-9 rounded-lg font-timer font-bold shrink-0 transition-colors ${form.correct_index === i ? "bg-[#138808] text-white" : "bg-slate-100 text-slate-500"}`}>{String.fromCharCode(65 + i)}</button>
                <Input data-testid={`q-option-${i}`} value={o} onChange={(e) => { const opts = [...form.options]; opts[i] = e.target.value; setForm({ ...form, options: opts }); }} placeholder={`Option ${String.fromCharCode(65 + i)}`} />
              </div>
            ))}
            <p className="text-xs text-slate-400">Click a letter to mark it as the correct answer.</p>
            <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Explanation</label><Input data-testid="q-explanation" className="mt-1.5" value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Points</label><Input data-testid="q-points" type="number" className="mt-1.5" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} /></div>
              <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Timer (s)</label><Input data-testid="q-timer" type="number" className="mt-1.5" value={form.timer} onChange={(e) => setForm({ ...form, timer: e.target.value })} /></div>
              <div>
                <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Difficulty</label>
                <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                  <SelectTrigger data-testid="q-difficulty" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["easy", "medium", "hard"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <button onClick={save} data-testid="q-save" className="w-full py-3 rounded-lg bg-[#003366] text-white font-bold hover:bg-[#00284f] transition-colors">Save Question</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-xl" data-testid="import-modal">
          <DialogHeader><DialogTitle className="font-heading">Bulk CSV Import</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Format: <code className="bg-slate-100 px-1.5 py-0.5 rounded">question,option_a,option_b,option_c,option_d,correct(a-d),explanation,points,timer,difficulty</code></p>
          <div className="flex flex-wrap gap-3">
            <button onClick={downloadTemplate} data-testid="import-download-template"
              className="px-4 py-2.5 rounded-lg border-2 border-[#003366] text-[#003366] text-sm font-bold hover:bg-[#003366] hover:text-white transition-colors inline-flex items-center gap-2">
              <FileDown className="w-4 h-4" /> Download Template CSV
            </button>
            <label data-testid="import-upload-file" className="px-4 py-2.5 rounded-lg border-2 border-[#138808] text-[#138808] text-sm font-bold hover:bg-[#138808] hover:text-white transition-colors inline-flex items-center gap-2 cursor-pointer">
              <FileUp className="w-4 h-4" /> {csvFileName ? csvFileName : "Upload CSV File"}
              <input type="file" accept=".csv" className="hidden" onChange={handleFileUpload} data-testid="import-file-input" />
            </label>
          </div>
          <Select value={importCat} onValueChange={setImportCat}>
            <SelectTrigger data-testid="import-category" className="mt-2"><SelectValue placeholder="Pick category" /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea data-testid="import-csv" rows={8} className="font-mono text-xs" placeholder={"question,option_a,option_b,option_c,option_d,correct\nWhich year did India gain independence?,1945,1946,1947,1950,c\n…or upload a CSV file above"} value={csvText} onChange={(e) => { setCsvText(e.target.value); setCsvFileName(""); }} />
          <button onClick={doImport} data-testid="import-submit" className="w-full py-3 rounded-lg bg-[#FF9933] text-white font-bold hover:bg-[#E6892E] transition-colors">Import Questions</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
